import * as React from 'react'

import { cn } from '@/lib/utils'

import type { NonFsdsGeneralV1IntroSegment } from '@/types/non-fsds-general-v1.type'

interface IntroStatementSectionProps {
  segments: NonFsdsGeneralV1IntroSegment[]
  scrollFill?: boolean
  className?: string
}

/**
 * General V1 Intro Statement — editorial paragraph on light canvas.
 *
 * Renders segments with navy (highlighted) or muted (unhighlighted) text.
 * When `scrollFill` is enabled, unhighlighted segments fill to navy as the
 * section scrolls into view. Reduced-motion users see all text at full
 * opacity immediately.
 */
export default function IntroStatementSection({
  segments,
  scrollFill = true,
  className,
}: IntroStatementSectionProps) {
  const sectionRef = React.useRef<HTMLElement>(null)
  const [fillProgress, setFillProgress] = React.useState(0)

  React.useEffect(() => {
    if (!scrollFill) {
      setFillProgress(0)
      return
    }
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setFillProgress(1)
      return
    }

    let ticking = false
    const handleScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const el = sectionRef.current
        if (!el) {
          ticking = false
          return
        }
        const rect = el.getBoundingClientRect()
        const windowHeight = window.innerHeight
        // Start filling when section top hits 80% of viewport, complete at 30%
        const start = windowHeight * 0.8
        const end = windowHeight * 0.3
        const progress = Math.min(Math.max((start - rect.top) / (start - end), 0), 1)
        setFillProgress(progress)
        ticking = false
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [scrollFill])

  if (!segments || segments.length === 0) return null

  // Build character-level fill: highlighted segments are always full,
  // unhighlighted fill proportionally based on scroll progress
  const totalUnhighlightedChars = segments
    .filter(s => !s.highlighted)
    .reduce((sum, s) => sum + s.text.length, 0)
  const charsToFill = Math.round(totalUnhighlightedChars * fillProgress)

  let filledSoFar = 0

  return (
    <section
      ref={sectionRef}
      data-testid="gv1-intro-statement"
      className={cn(
        'bg-[var(--gv1-bg-surface)] pb-[60px] pt-[119px]',
        className
      )}
    >
      <div className="gv1-container">
        {/* Screen-reader accessible full text */}
        <p className="sr-only">
          {segments.map(s => s.text).join('')}
        </p>

        {/* Visual animated text — hidden from assistive tech */}
        <p
          data-testid="gv1-intro-animated-text"
          data-fill-progress={fillProgress.toFixed(3)}
          className="max-w-[1180px] text-[32px] font-semibold leading-[44px] tracking-[-0.02em]"
          style={{ fontFamily: 'var(--gv1-font-heading)' }}
          aria-hidden="true"
        >
          {segments.map((segment, idx) => {
            if (segment.highlighted) {
              return (
                <span key={idx} className="text-[var(--gv1-bg-primary)]">
                  {segment.text}
                </span>
              )
            }

            // Unhighlighted: split into filled and unfilled characters
            const remaining = charsToFill - filledSoFar
            const fillCount = Math.min(Math.max(remaining, 0), segment.text.length)
            filledSoFar += fillCount

            if (fillCount >= segment.text.length) {
              return (
                <span key={idx} className="text-[var(--gv1-bg-primary)]">
                  {segment.text}
                </span>
              )
            }

            if (fillCount <= 0) {
              return (
                <span key={idx} className="text-slate-950/20">
                  {segment.text}
                </span>
              )
            }

            return (
              <React.Fragment key={idx}>
                <span className="text-[var(--gv1-bg-primary)]">
                  {segment.text.slice(0, fillCount)}
                </span>
                <span className="text-slate-950/20">
                  {segment.text.slice(fillCount)}
                </span>
              </React.Fragment>
            )
          })}
        </p>
      </div>
    </section>
  )
}
